import { Enum_Relatedinfo_Viewvariant, RelatedInfo } from "@/app/lib/generated/graphql-types";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { useCallback } from "react";
import Grid from "@mui/material/Grid2";
import styles from './related-infos.module.css';
import RelatedInfoViewCompact from "@/app/features/shared/related-infos/ui/RelatedInfoViewCompact";
import RelatedInfoViewExtensive from "@/app/features/shared/related-infos/ui/ExtensiveViewVariant";
import RelatedInfoViewPersonalContact from "@/app/features/shared/related-infos/ui/PersonalContactViewVariant";
import { RELATED_INFOS_SPACING } from "@/app/features/shared/related-infos/ui/spacing";

interface RelatedInfosViewProps {
    title?: string;
    relatedInfos: RelatedInfo[]
}

export default function RelatedInfosView({ title, relatedInfos }: RelatedInfosViewProps) {

    const colSpanForViewVariant = (variant: Enum_Relatedinfo_Viewvariant) => {
        switch (variant) {
            case Enum_Relatedinfo_Viewvariant.Compact:
                return 1;
            case Enum_Relatedinfo_Viewvariant.Extensive:
                return 2;
            case Enum_Relatedinfo_Viewvariant.Personal:
                return 2;
            default:
                throw new Error(`Unknown view variant: ${variant}`);
        }
    }

    const gridItemSize = useCallback((info: RelatedInfo, index: number) => {
        if (info.colSpan && info.colSpan > 0) {
            return info.colSpan;
        }
        if (relatedInfos.length === 1) {
            return 2;
        }

        const variantSpecificColSpan = colSpanForViewVariant(info.viewVariant);
        const allColumnSpan = relatedInfos.map(r =>  r.colSpan || colSpanForViewVariant(r.viewVariant)).reduce((a, b) => a + b, 0);

        if (variantSpecificColSpan == 1 && allColumnSpan % 2 != 0 && index == relatedInfos.length - 1) {
            return 2;
        } else {
            return variantSpecificColSpan;
        }
    }, [relatedInfos])

    const viewForInfo = useCallback((info: RelatedInfo) => {
        switch (info.viewVariant) {
            case Enum_Relatedinfo_Viewvariant.Compact:
                return <RelatedInfoViewCompact info={info} />;
            case Enum_Relatedinfo_Viewvariant.Extensive:
                return <RelatedInfoViewExtensive info={info} />;
            case Enum_Relatedinfo_Viewvariant.Personal:
                return <RelatedInfoViewPersonalContact info={info} />;
            default:
                throw new Error(`Unknown view variant: ${info.viewVariant}`);
        }
    }, []);


    return (
        <Box className={styles.relatedInfosContainer}>
            <Box className={styles.relatedInfosInner}>
                <Stack spacing={RELATED_INFOS_SPACING.section} className="w-full h-full">
                    {title && (
                        <Typography component="h2" className={styles.relatedInfosTitle} variant="h1" color="text.secondary">
                            {title}
                        </Typography>
                    )}

                    <Grid
                        container
                        columns={{ xs: 1, md: 2 }}
                        rowSpacing={RELATED_INFOS_SPACING.grid}
                        columnSpacing={RELATED_INFOS_SPACING.grid}
                    >
                        {relatedInfos.map((info, index) =>
                            <span key={index} className="w-full">
                                {index > 0 && (
                                    <Divider />
                                )}
                                <Grid key={index} size={gridItemSize(info, index)}>
                                    {viewForInfo(info)}
                                </Grid>
                            </span>
                        )}
                    </Grid>
                </Stack>
            </Box>
        </Box>
    )
}
